import { SelectDocumentModal } from "@/components/features/matter/file-repo/signature-requests/SelectDocumentModal";
import { SelectMatterForSignatureRequestModal } from "@/components/features/matter/file-repo/signature-requests/SelectMatterForSignatureRequestModal";
import { SelectFirmFileModal } from "@/components/features/settings/firm/SelectFirmFileModal";
import {
  LoadingSpinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query";
import {
  retrieveFirmFilesOptions,
  useCreateFirmFile,
} from "@/lib/data/react-query/firm/firm-file";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useCreateMatterFile } from "@/lib/data/react-query/matters/matter-file";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { FirmFile, MatterFile, MatterTemplate } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getFirmFileBaseName } from "@/lib/utils/firmFile";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronRight,
  FileStack,
  FolderSearch,
  FolderSync,
  Upload,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { SignatureRequestDocumentPreview } from "./SignatureRequestDocumentPreview";
import { SelectTemplateSlideover } from "./SelectTemplateSlideover";

interface SelectDocumentStepProps {
  slideoverOpen: boolean;
  matterId?: string;
  isGlobalMode: boolean;
  selectedMatterFileId: MatterFile["id"];
  selectedMatterTemplateId: MatterTemplate["id"];
  selectedFirmFile: FirmFile | null;
  setSelectedMatterId: (matterId: string) => void;
  onMatterFileSelected: (file: MatterFile) => void;
  onMatterTemplateSelected: (id: MatterTemplate["id"]) => void;
  onDirectFirmFileSelected: (file: FirmFile) => void;
  onGlobalSignatureFlowKindSet: (kind: "matter" | "firm") => void;
}

const signatureRequestEnabledFileTypes = ["docx", "pdf"];

const sourceOptionCardClassName =
  "bg-card group flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left shadow-sm transition-colors";

export const SelectDocumentStep = ({
  slideoverOpen,
  matterId,
  isGlobalMode,
  selectedMatterFileId,
  selectedMatterTemplateId,
  selectedFirmFile,
  setSelectedMatterId,
  onMatterFileSelected,
  onMatterTemplateSelected,
  onDirectFirmFileSelected,
  onGlobalSignatureFlowKindSet,
}: SelectDocumentStepProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const firmUploadFileInputRef = useRef<HTMLInputElement | null>(null);
  const { hasActionPermission } = useUser();
  const [documentSourceTab, setDocumentSourceTab] = useState<"matter" | "firm">(
    "matter",
  );
  /** Matter APIs only when using the Matter tab (or matter-scoped slideover). Firm tab = firm-only flow. */
  const matterUiEnabled = !isGlobalMode || documentSourceTab === "matter";
  /** Matter settings (template list): Matter tab or matter-scoped slideover only — not the global Firm tab. */
  const matterSettingsQueryEnabled =
    slideoverOpen && (!isGlobalMode || documentSourceTab === "matter");
  const {
    data: matterData,
    isLoading,
    refetch: refetchMatter,
  } = useQuery({
    ...retrieveMatterQueryOptions(matterId ?? ""),
    enabled: Boolean(matterId) && matterUiEnabled,
  });
  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    refetch: refetchMatterSettings,
  } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: matterSettingsQueryEnabled,
  });
  const createMatterFile = useCreateMatterFile();
  const createFirmFile = useCreateFirmFile({ showToast: false });
  const { data: firmFilesForGlobalStep, refetch: refetchFirmFiles } = useQuery(
    retrieveFirmFilesOptions(slideoverOpen && isGlobalMode),
  );
  const [isSelectDocumentOpen, setIsSelectDocumentOpen] = useState(false);
  const [isSelectMatterOpen, setIsSelectMatterOpen] = useState(false);
  const [isSelectFirmFileOpen, setIsSelectFirmFileOpen] = useState(false);
  const [firmFilePickIntent, setFirmFilePickIntent] = useState<
    "import" | "direct" | null
  >(null);
  const [isSelectTemplateOpen, setIsSelectTemplateOpen] = useState(false);
  const [isImportingFirmFile, setIsImportingFirmFile] = useState(false);
  const matter = matterData?.data;
  const firmFilesList = firmFilesForGlobalStep?.data ?? [];
  const isProcessingDocument =
    createMatterFile.isPending ||
    createFirmFile.isPending ||
    isImportingFirmFile;
  const requiresSelectedMatter = isGlobalMode && !matter;

  const canCreateFirmFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.create,
  );

  useEffect(() => {
    if (slideoverOpen && isGlobalMode) {
      setDocumentSourceTab("matter");
      onGlobalSignatureFlowKindSet("matter");
    }
  }, [isGlobalMode, onGlobalSignatureFlowKindSet, slideoverOpen]);

  const selectedFile = useMemo(() => {
    if (!matter || !selectedMatterFileId) {
      return null;
    }

    return (
      matter.files.find((file) => file.id === selectedMatterFileId) || null
    );
  }, [matter, selectedMatterFileId]);
  const selectedTemplate = useMemo(
    () =>
      matterSettingsData?.data.templates.find(
        (template) => template.id === selectedMatterTemplateId,
      ) ?? null,
    [matterSettingsData?.data.templates, selectedMatterTemplateId],
  );
  const selectedDocument = selectedFirmFile
    ? { type: "firm-file" as const, document: selectedFirmFile }
    : selectedFile
      ? { type: "matter-file" as const, document: selectedFile }
      : selectedTemplate
        ? { type: "matter-template" as const, document: selectedTemplate }
        : null;
  const buildUniqueMatterFileName = (fileName: string) => {
    if (!matter) {
      return fileName;
    }

    const existingNames = new Set(
      matter.files.map((file) => file.name.toLowerCase()),
    );

    if (!existingNames.has(fileName.toLowerCase())) {
      return fileName;
    }

    const { fileName: baseName, fileExtension } = getFileNameParts(fileName);
    let index = 1;
    let candidate = `${baseName} (${index})${fileExtension ? `.${fileExtension}` : ""}`;

    while (existingNames.has(candidate.toLowerCase())) {
      index += 1;
      candidate = `${baseName} (${index})${fileExtension ? `.${fileExtension}` : ""}`;
    }

    return candidate;
  };

  const buildUniqueFirmFileBaseName = (baseName: string) => {
    const trimmed = baseName.trim() || "document";
    const existingNames = new Set(
      firmFilesList.map((file) => getFirmFileBaseName(file).toLowerCase()),
    );

    if (!existingNames.has(trimmed.toLowerCase())) {
      return trimmed;
    }

    let index = 1;
    let candidate = `${trimmed} (${index})`;

    while (existingNames.has(candidate.toLowerCase())) {
      index += 1;
      candidate = `${trimmed} (${index})`;
    }

    return candidate;
  };

  const handleMatterDocumentSelected = (files: MatterFile[]) => {
    if (files.length === 0) {
      return;
    }

    const file = files[0];
    onGlobalSignatureFlowKindSet("matter");
    onMatterFileSelected(file);
  };

  const handleFirmTabUploadDocument = async (fileList: FileList | null) => {
    const file = fileList?.[0];

    if (!file || !canCreateFirmFiles) {
      return;
    }

    const extension = (
      getFileNameParts(file.name).fileExtension || ""
    ).toLowerCase();

    if (extension !== "docx" && extension !== "pdf") {
      toast.error("Only PDF or DOCX files can be used for signature requests.");
      return;
    }

    try {
      const { fileName: baseFromName } = getFileNameParts(file.name);
      const firmFileBaseName = buildUniqueFirmFileBaseName(baseFromName);
      const firmFileExtension = extension;
      const created = await createFirmFile.mutateAsync({
        idLocation: "",
        isFolder: false,
        name: firmFileBaseName,
        file: new File([file], `${firmFileBaseName}.${firmFileExtension}`, {
          type: file.type,
        }),
      });
      onGlobalSignatureFlowKindSet("firm");
      onDirectFirmFileSelected(created.data);
      toast.success("Firm document uploaded.");
    } catch (error) {
      console.error("Failed to upload firm signature request document", error);
      toast.error("Unable to upload document. Please try again.");
    }
  };

  const handleUploadDocument = async (fileList: FileList | null) => {
    const file = fileList?.[0];

    if (!file || !matterId || !matter) {
      return;
    }

    try {
      const matterFileName = buildUniqueMatterFileName(file.name);
      const uploadFileType = typeof file.type === "string" ? file.type : "";
      const uploadFile =
        matterFileName === file.name
          ? file
          : new File([file], matterFileName, { type: uploadFileType });

      const created = await createMatterFile.mutateAsync({
        matterId,
        isFolder: false,
        idLocation: "",
        name: matterFileName,
        file: uploadFile,
      });
      onGlobalSignatureFlowKindSet("matter");
      onMatterFileSelected(created.data);
    } catch (error) {
      console.error("Failed to upload signature request document", error);
      toast.error("Unable to upload document. Please try again.");
    }
  };

  const handleFirmFileSelected = async (files: FirmFile[]) => {
    if (files.length === 0 || !matter || !matterId || isImportingFirmFile) {
      return;
    }

    const firmFile = files[0];

    if (!firmFile.presignedUrlKey) {
      toast.error("Unable to import the selected firm file.");
      return;
    }

    const getFirmFileName = (selectedFirmFile: FirmFile) => {
      const baseName = selectedFirmFile.name.trim() || "firm-file";
      const hasExtension = Boolean(getFileNameParts(baseName).fileExtension);

      if (hasExtension) {
        return baseName;
      }

      const keyName = selectedFirmFile.presignedUrlKey?.split("/").pop();
      const keyExtension = keyName
        ? getFileNameParts(keyName).fileExtension
        : "";

      return keyExtension ? `${baseName}.${keyExtension}` : baseName;
    };

    setIsImportingFirmFile(true);

    try {
      const fileName = buildUniqueMatterFileName(getFirmFileName(firmFile));
      const presigned = await generateS3ItemPreSignedUrlV1("firm-file")({
        clientMethod: "get_object",
        key: firmFile.presignedUrlKey,
      });
      const response = await fetch(presigned.data.presignedUrl, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("firm-file-download-failed");
      }

      const blob = await response.blob();
      const responseContentType = response.headers.get("content-type");
      const contentType =
        typeof responseContentType === "string"
          ? responseContentType
          : typeof blob.type === "string" && blob.type.length > 0
            ? blob.type
            : undefined;
      const file = new File([blob], fileName, {
        type: contentType,
      });
      const created = await createMatterFile.mutateAsync({
        matterId,
        isFolder: false,
        idLocation: "",
        name: fileName,
        file,
      });
      onGlobalSignatureFlowKindSet("matter");
      onMatterFileSelected(created.data);
      setIsSelectFirmFileOpen(false);
      setFirmFilePickIntent(null);
    } catch (error) {
      console.error("Failed to import firm file", error);
      toast.error("Unable to import firm file. Please try again.");
    } finally {
      setIsImportingFirmFile(false);
    }
  };

  const handleFirmFilesFromSlideover = (files: FirmFile[]) => {
    if (files.length === 0) {
      return;
    }

    if (firmFilePickIntent === "direct") {
      onGlobalSignatureFlowKindSet("firm");
      onDirectFirmFileSelected(files[0]);
      setIsSelectFirmFileOpen(false);
      setFirmFilePickIntent(null);
      return;
    }

    setFirmFilePickIntent(null);
    void handleFirmFileSelected(files);
  };

  const handleTemplateSelected = (template: MatterTemplate) => {
    onGlobalSignatureFlowKindSet(documentSourceTab);
    onMatterTemplateSelected(template.id);
  };

  const handleMatterFileSaved = async (matterFileId: string) => {
    const refreshed = await refetchMatter();
    const updatedFile = refreshed.data?.data.files.find(
      (file) => file.id === matterFileId,
    );
    if (!updatedFile) {
      return;
    }
    onMatterFileSelected(updatedFile);
  };

  const handleFirmFileSaved = async (firmFileId: string) => {
    const refreshed = await refetchFirmFiles();
    const updatedFile = refreshed.data?.data.find(
      (file) => file.id === firmFileId,
    );
    if (!updatedFile) {
      return;
    }
    onDirectFirmFileSelected(updatedFile);
  };

  const handleTemplateSaved = async (id: MatterTemplate["id"]) => {
    const refreshed = await refetchMatterSettings();
    const updatedTemplate = refreshed.data?.data.templates.find(
      (template) => template.id === id,
    );
    if (!updatedTemplate) {
      return;
    }
    onMatterTemplateSelected(updatedTemplate.id);
  };

  const matterTabOptionDisabled =
    requiresSelectedMatter || isProcessingDocument;

  /**
   * Global “Create signature request”: templates still generate into a matter, so we require
   * a selected matter first (same as uploads / matter files). Matter-scoped slideover already
   * has an implicit matter.
   */
  const templateSearchDisabled = isProcessingDocument || requiresSelectedMatter;

  const matterContextOptionDisabled = isProcessingDocument;

  if (
    (Boolean(matterId) && matterUiEnabled && isLoading) ||
    (matterSettingsQueryEnabled && isMatterSettingsLoading)
  ) {
    return (
      <div className="flex h-full min-h-0 w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="bg-card grid h-full min-h-0 w-full overflow-hidden lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="min-h-0 min-w-0 bg-slate-50/40">
          <SignatureRequestDocumentPreview
            matterId={matterId ?? ""}
            selectedDocument={selectedDocument}
            canEditDocumentFiles={false}
            emptyTitle="Select a file to preview."
            emptyDescription="Use one of the options on the right to choose a document."
            onMatterFileSaved={handleMatterFileSaved}
            onFirmFileSaved={handleFirmFileSaved}
            onTemplateSaved={handleTemplateSaved}
          />
        </div>

        <div className="border-border/80 bg-card flex h-full min-h-0 flex-col border-l">
          <div className="p-4 pb-3">
            <Typography className="text-xl font-semibold">
              Select document
            </Typography>
            <Typography className="text-muted-foreground mt-1 text-xs">
              Choose how you want to add a document for signatures.
            </Typography>
          </div>

          {!isGlobalMode ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3.5">
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  className={cn(
                    sourceOptionCardClassName,
                    "border-border/80 hover:border-primary/40 hover:bg-primary/5",
                    templateSearchDisabled && "cursor-not-allowed opacity-70",
                  )}
                  disabled={templateSearchDisabled}
                  onClick={() => {
                    setIsSelectTemplateOpen(true);
                  }}
                >
                  <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                    <FileStack className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Typography className="text-base font-semibold">
                      Search templates
                    </Typography>
                    <Typography className="text-muted-foreground mt-0.5 text-sm">
                      Use an existing template
                    </Typography>
                  </div>
                  <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                </button>

                <button
                  type="button"
                  className={cn(
                    sourceOptionCardClassName,
                    "border-border/80 hover:border-primary/40 hover:bg-primary/5 border-dashed",
                    matterContextOptionDisabled &&
                      "cursor-not-allowed opacity-70",
                  )}
                  disabled={matterContextOptionDisabled}
                  onClick={() => {
                    if (requiresSelectedMatter) {
                      setIsSelectMatterOpen(true);
                      return;
                    }
                    fileInputRef.current?.click();
                  }}
                >
                  <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                    {isProcessingDocument ? (
                      <LoadingSpinner className="h-4.5 w-4.5" />
                    ) : (
                      <Upload className="h-5 w-5" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Typography className="text-base font-semibold">
                      Upload document
                    </Typography>
                    <Typography className="text-muted-foreground mt-0.5 text-sm">
                      {requiresSelectedMatter
                        ? "Select a matter first"
                        : "Upload a PDF, Word, or image file"}
                    </Typography>
                  </div>
                  <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,.pdf"
                  className="hidden"
                  onChange={async (event) => {
                    await handleUploadDocument(event.target.files);
                    event.target.value = "";
                  }}
                />

                <button
                  type="button"
                  className={cn(
                    sourceOptionCardClassName,
                    "border-border/80 hover:border-primary/40 hover:bg-primary/5",
                  )}
                  onClick={() => {
                    setIsSelectDocumentOpen(true);
                  }}
                >
                  <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                    <FolderSearch className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Typography className="text-base font-semibold">
                      Search matter files
                    </Typography>
                    <Typography className="text-muted-foreground mt-0.5 text-sm">
                      Find an existing document in this matter
                    </Typography>
                  </div>
                  <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                </button>

                <button
                  type="button"
                  className={cn(
                    sourceOptionCardClassName,
                    "border-border/80 hover:border-primary/40 hover:bg-primary/5",
                    matterContextOptionDisabled &&
                      "cursor-not-allowed opacity-70",
                  )}
                  disabled={matterContextOptionDisabled}
                  onClick={() => {
                    if (requiresSelectedMatter) {
                      setIsSelectMatterOpen(true);
                      return;
                    }
                    setFirmFilePickIntent("import");
                    setIsSelectFirmFileOpen(true);
                  }}
                >
                  <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Typography className="text-base font-semibold">
                      Search firm files
                    </Typography>
                    <Typography className="text-muted-foreground mt-0.5 text-sm">
                      {requiresSelectedMatter
                        ? "Select a matter first"
                        : "Find an existing document in firm files"}
                    </Typography>
                  </div>
                  <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                </button>
              </div>
            </div>
          ) : (
            <Tabs
              value={documentSourceTab}
              onValueChange={(value) => {
                const next = value as "matter" | "firm";
                setDocumentSourceTab(next);
                onGlobalSignatureFlowKindSet(next);
              }}
              className="flex min-h-0 min-w-0 flex-1 flex-col"
            >
              <div className="border-border/80 border-b px-4">
                <TabsList className="h-9 w-full justify-start gap-0 rounded-none bg-transparent p-0">
                  <TabsTrigger
                    value="matter"
                    className="data-[state=active]:border-primary flex-1 rounded-none border-b-2 border-transparent bg-transparent px-3 pt-1 pb-2 text-sm font-medium shadow-none data-[state=active]:bg-transparent sm:flex-none"
                  >
                    Matter
                  </TabsTrigger>
                  <TabsTrigger
                    value="firm"
                    className="data-[state=active]:border-primary flex-1 rounded-none border-b-2 border-transparent bg-transparent px-3 pt-1 pb-2 text-sm font-medium shadow-none data-[state=active]:bg-transparent sm:flex-none"
                  >
                    Firm
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent
                value="matter"
                className="mt-0 min-h-0 flex-1 overflow-y-auto px-3.5 pb-3.5 data-[state=inactive]:hidden"
              >
                <div className="flex flex-col gap-3 pt-3">
                  <button
                    type="button"
                    className={cn(
                      sourceOptionCardClassName,
                      "border-border/80 hover:border-primary/40 hover:bg-primary/5",
                    )}
                    onClick={() => {
                      setIsSelectMatterOpen(true);
                    }}
                  >
                    <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                      <FolderSync className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Typography className="text-base font-semibold">
                        {matter ? "Change matter" : "Select matter"}
                      </Typography>
                      <Typography className="text-muted-foreground mt-0.5 text-sm">
                        {matter
                          ? matter.title || "Untitled matter"
                          : "Choose the matter for matter files, uploads, and signers"}
                      </Typography>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                  </button>

                  <button
                    type="button"
                    className={cn(
                      sourceOptionCardClassName,
                      "border-border/80 hover:border-primary/40 hover:bg-primary/5",
                      templateSearchDisabled && "cursor-not-allowed opacity-60",
                    )}
                    disabled={templateSearchDisabled}
                    onClick={() => {
                      setIsSelectTemplateOpen(true);
                    }}
                  >
                    <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                      <FileStack className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Typography className="text-base font-semibold">
                        Search templates
                      </Typography>
                      <Typography className="text-muted-foreground mt-0.5 text-sm">
                        {requiresSelectedMatter
                          ? "Select a matter first"
                          : "Use an existing template"}
                      </Typography>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                  </button>

                  <button
                    type="button"
                    className={cn(
                      sourceOptionCardClassName,
                      "border-border/80 hover:border-primary/40 hover:bg-primary/5 border-dashed",
                      matterTabOptionDisabled &&
                        "cursor-not-allowed opacity-60",
                    )}
                    disabled={matterTabOptionDisabled}
                    onClick={() => {
                      if (requiresSelectedMatter) {
                        return;
                      }
                      fileInputRef.current?.click();
                    }}
                  >
                    <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                      {isProcessingDocument ? (
                        <LoadingSpinner className="h-4.5 w-4.5" />
                      ) : (
                        <Upload className="h-5 w-5" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Typography className="text-base font-semibold">
                        Upload document
                      </Typography>
                      <Typography className="text-muted-foreground mt-0.5 text-sm">
                        {requiresSelectedMatter
                          ? "Select a matter first"
                          : "Upload a PDF, Word, or image file"}
                      </Typography>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".docx,.pdf"
                    className="hidden"
                    onChange={async (event) => {
                      await handleUploadDocument(event.target.files);
                      event.target.value = "";
                    }}
                  />

                  <button
                    type="button"
                    className={cn(
                      sourceOptionCardClassName,
                      "border-border/80 hover:border-primary/40 hover:bg-primary/5",
                      matterTabOptionDisabled &&
                        "cursor-not-allowed opacity-60",
                    )}
                    disabled={matterTabOptionDisabled}
                    onClick={() => {
                      if (requiresSelectedMatter) {
                        return;
                      }
                      setIsSelectDocumentOpen(true);
                    }}
                  >
                    <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                      <FolderSearch className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Typography className="text-base font-semibold">
                        Search matter files
                      </Typography>
                      <Typography className="text-muted-foreground mt-0.5 text-sm">
                        {requiresSelectedMatter
                          ? "Select a matter first"
                          : "Find an existing document in the selected matter"}
                      </Typography>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                  </button>

                  <button
                    type="button"
                    className={cn(
                      sourceOptionCardClassName,
                      "border-border/80 hover:border-primary/40 hover:bg-primary/5",
                      matterTabOptionDisabled &&
                        "cursor-not-allowed opacity-60",
                    )}
                    disabled={matterTabOptionDisabled}
                    onClick={() => {
                      if (requiresSelectedMatter) {
                        return;
                      }
                      setFirmFilePickIntent("import");
                      setIsSelectFirmFileOpen(true);
                    }}
                  >
                    <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Typography className="text-base font-semibold">
                        Search firm files
                      </Typography>
                      <Typography className="text-muted-foreground mt-0.5 text-sm">
                        {requiresSelectedMatter
                          ? "Select a matter first"
                          : "Find an existing document in firm files"}
                      </Typography>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                  </button>
                </div>
              </TabsContent>

              <TabsContent
                value="firm"
                className="mt-0 min-h-0 flex-1 overflow-y-auto px-3.5 pb-3.5 data-[state=inactive]:hidden"
              >
                <div className="flex flex-col gap-3 pt-3">
                  <button
                    type="button"
                    className={cn(
                      sourceOptionCardClassName,
                      "border-border/80 hover:border-primary/40 hover:bg-primary/5 border-dashed",
                      (isProcessingDocument || !canCreateFirmFiles) &&
                        "cursor-not-allowed opacity-60",
                    )}
                    disabled={isProcessingDocument || !canCreateFirmFiles}
                    onClick={() => {
                      firmUploadFileInputRef.current?.click();
                    }}
                  >
                    <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                      {createFirmFile.isPending ? (
                        <LoadingSpinner className="h-4.5 w-4.5" />
                      ) : (
                        <Upload className="h-5 w-5" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Typography className="text-base font-semibold">
                        Upload document
                      </Typography>
                      <Typography className="text-muted-foreground mt-0.5 text-sm">
                        {!canCreateFirmFiles
                          ? "You don’t have permission to upload firm files"
                          : "Upload a PDF or DOCX file for this firm signature request"}
                      </Typography>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                  </button>

                  <button
                    type="button"
                    className={cn(
                      sourceOptionCardClassName,
                      "border-border/80 hover:border-primary/40 hover:bg-primary/5",
                      isProcessingDocument && "cursor-not-allowed opacity-60",
                    )}
                    disabled={isProcessingDocument}
                    onClick={() => {
                      setFirmFilePickIntent("direct");
                      setIsSelectFirmFileOpen(true);
                    }}
                  >
                    <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Typography className="text-base font-semibold">
                        Search firm files
                      </Typography>
                      <Typography className="text-muted-foreground mt-0.5 text-sm">
                        Browse files in your firm library
                      </Typography>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4.5 w-4.5 shrink-0" />
                  </button>

                  <input
                    ref={firmUploadFileInputRef}
                    type="file"
                    accept=".docx,.pdf"
                    className="hidden"
                    onChange={async (event) => {
                      await handleFirmTabUploadDocument(event.target.files);
                      event.target.value = "";
                    }}
                  />
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {matter ? (
        <SelectDocumentModal
          open={isSelectDocumentOpen}
          setOpen={setIsSelectDocumentOpen}
          matter={matter}
          onDocumentSelected={handleMatterDocumentSelected}
          enabledFileTypes={signatureRequestEnabledFileTypes}
          confirmActionLabel="Select"
        />
      ) : null}

      <SelectFirmFileModal
        open={isSelectFirmFileOpen}
        setOpen={(nextOpen) => {
          setIsSelectFirmFileOpen(nextOpen);
          if (!nextOpen) {
            setFirmFilePickIntent(null);
          }
        }}
        onFirmFilesSelected={handleFirmFilesFromSlideover}
        multiple={false}
        title="Select firm file"
        description={
          firmFilePickIntent === "direct"
            ? "Choose a firm file for this signature request."
            : "Choose a firm file to import for this signature request."
        }
        enabledFileTypes={signatureRequestEnabledFileTypes}
      />

      <SelectTemplateSlideover
        open={isSelectTemplateOpen}
        setOpen={setIsSelectTemplateOpen}
        templates={matterSettingsData?.data.templates ?? []}
        isLoading={isMatterSettingsLoading}
        onTemplateSelected={handleTemplateSelected}
      />

      {isGlobalMode ? (
        <SelectMatterForSignatureRequestModal
          open={isSelectMatterOpen}
          setOpen={setIsSelectMatterOpen}
          selectedMatterId={matterId}
          onMatterSelected={(selectedMatter) => {
            setSelectedMatterId(selectedMatter.matterId);
          }}
        />
      ) : null}
    </>
  );
};
